import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from 'react';
import { CONSENT_ELEMENT_TYPES } from '../../constants/consent_element_types.constants';
export const ConsentElementMarker = ({ element, onUpdate, onDelete, onConfigureClick, }) => {
    const MIN_WIDTH_PCT = 0.05;
    const MIN_HEIGHT_PCT = 0.02;
    const def = CONSENT_ELEMENT_TYPES[element.type];
    const containerRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragStartRef = useRef(null);
    const handleMouseDown = (e) => {
        if (e.target.closest('[data-action]')) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        const parent = containerRef.current?.parentElement?.parentElement;
        if (!parent) {
            return;
        }
        const parentRect = parent.getBoundingClientRect();
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            pos: { ...element.position },
        };
        setIsDragging(true);
        const onMouseMove = (moveEvent) => {
            if (!dragStartRef.current) {
                return;
            }
            const dx = (moveEvent.clientX - dragStartRef.current.mouseX) / parentRect.width;
            const dy = (moveEvent.clientY - dragStartRef.current.mouseY) / parentRect.height;
            const newX = Math.max(0, Math.min(1 - element.position.widthPct, dragStartRef.current.pos.xPct + dx));
            const newY = Math.max(0, Math.min(1 - element.position.heightPct, dragStartRef.current.pos.yPct + dy));
            onUpdate({
                ...element,
                position: { ...element.position, xPct: newX, yPct: newY },
            });
        };
        const onMouseUp = () => {
            setIsDragging(false);
            dragStartRef.current = null;
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };
    const handleResizeMouseDown = (e, resizeMode = 'both') => {
        e.preventDefault();
        e.stopPropagation();
        const parent = containerRef.current?.parentElement?.parentElement;
        if (!parent) {
            return;
        }
        const parentRect = parent.getBoundingClientRect();
        const startPos = { ...element.position };
        const startX = e.clientX;
        const startY = e.clientY;
        const onMouseMove = (moveEvent) => {
            const dw = (moveEvent.clientX - startX) / parentRect.width;
            const dh = (moveEvent.clientY - startY) / parentRect.height;
            const nextWidth = resizeMode === 'height' ? startPos.widthPct : startPos.widthPct + dw;
            const nextHeight = resizeMode === 'width' ? startPos.heightPct : startPos.heightPct + dh;
            const newW = Math.max(MIN_WIDTH_PCT, Math.min(1 - startPos.xPct, nextWidth));
            const newH = Math.max(MIN_HEIGHT_PCT, Math.min(1 - startPos.yPct, nextHeight));
            onUpdate({
                ...element,
                position: { ...element.position, widthPct: newW, heightPct: newH },
            });
        };
        const onMouseUp = () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };
    return (_jsxs("div", { ref: containerRef, "data-testid": `consent-element-marker-${element.id}`, style: {
            position: 'absolute',
            left: `${element.position.xPct * 100}%`,
            top: `${element.position.yPct * 100}%`,
            width: `${element.position.widthPct * 100}%`,
            height: `${element.position.heightPct * 100}%`,
            cursor: isDragging ? 'grabbing' : 'grab',
            userSelect: 'none',
        }, onMouseDown: handleMouseDown, children: [_jsxs("div", { style: { borderColor: def.color }, className: 'relative flex h-full w-full flex-col overflow-hidden rounded border-2 bg-white bg-opacity-90', children: [_jsxs("div", { style: { backgroundColor: def.color }, className: 'flex items-center justify-between px-1 py-0.5', "data-testid": `element-header-${element.id}`, children: [_jsx("span", { className: 'truncate text-xs font-semibold text-white', children: def.label }), _jsxs("div", { className: 'flex items-center gap-0.5', children: [_jsx("button", { "data-action": 'configure', "data-testid": `configure-element-${element.id}`, className: 'rounded p-0.5 text-white hover:bg-white hover:bg-opacity-20', onClick: (e) => {
                                            e.stopPropagation();
                                            onConfigureClick(element);
                                        }, title: 'Configure', children: "\u2699" }), _jsx("button", { "data-action": 'delete', "data-testid": `delete-element-${element.id}`, className: 'rounded p-0.5 text-white hover:bg-white hover:bg-opacity-20', onClick: (e) => {
                                            e.stopPropagation();
                                            onDelete(element.id);
                                        }, title: 'Delete', children: "\u00D7" })] })] }), _jsx("div", { className: 'flex flex-1 items-center justify-center overflow-hidden px-1', children: _jsx("span", { className: 'truncate text-xs text-neutral-600', children: element.config.ec__label || element.config.ec__key }) })] }), _jsx("div", { "data-action": 'resize', "data-testid": `resize-handle-width-${element.id}`, style: {
                    position: 'absolute',
                    top: 0,
                    right: -4,
                    width: 8,
                    height: '100%',
                    cursor: 'ew-resize',
                }, onMouseDown: (e) => handleResizeMouseDown(e, 'width') }), _jsx("div", { "data-action": 'resize', "data-testid": `resize-handle-height-${element.id}`, style: {
                    position: 'absolute',
                    left: 0,
                    bottom: -4,
                    width: '100%',
                    height: 8,
                    cursor: 'ns-resize',
                }, onMouseDown: (e) => handleResizeMouseDown(e, 'height') }), _jsx("div", { "data-action": 'resize', "data-testid": `resize-handle-${element.id}`, style: {
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 10,
                    height: 10,
                    cursor: 'se-resize',
                    backgroundColor: def.color,
                    borderRadius: '2px 0 2px 0',
                }, onMouseDown: handleResizeMouseDown })] }));
};
